import { Button, CircularProgress } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { it } from "date-fns/locale";
import { SnackbarProvider, closeSnackbar, useSnackbar } from "notistack";
import { lazy, StrictMode, Suspense, useEffect } from "react";
import { AuthProvider, useAuth } from "react-oidc-context";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import Layout from "./components/Layout";
import { oidcConfig } from "./services/user-manager.service";
import { eliPartnerProgramTheme } from "./theme/eliPartnerProgramTheme";
import { withAuthenticationRequired } from "./utils/withAuthenticationRequired";
import { RouterProvider } from "./context/RouterContext";
import { UserProvider } from "./context/UserContext";

const Vendite = lazy(() => import("./pages/Vendite"));
const Spedizioni = lazy(() => import("./pages/Spedizioni"));
const Logout = lazy(() => import("./pages/Logout"));
const SchedaPuntoVendita = lazy(() => import("./pages/SchedaPuntoVendita"));
const ImmaginiPuntoVendita = lazy(() => import("./pages/ImmaginiPuntoVendita"));
const RepositoryImmagini = lazy(() => import("./pages/RepositoryImmagini"));
const DownloadImages = lazy(() => import("./pages/DownloadImages"));
const Statistiche = lazy(() => import("./pages/Statistiche"));
const Visitatori = lazy(() => import("./components/Visitatori"));
const Acquisti = lazy(() => import("./components/Acquisti"));
const CarrelliAbbandonati = lazy(
  () => import("./components/CarrelliAbbandonati"),
);
const Ordinato = lazy(() => import("./components/Ordinato"));
const BestSeller = lazy(() => import("./components/BestSeller"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={eliPartnerProgramTheme}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
            <SnackbarProvider
              maxSnack={3}
              action={(snackbarId) => (
                <Button
                  variant="text"
                  color="inherit"
                  onClick={() => closeSnackbar(snackbarId)}
                >
                  Dismiss
                </Button>
              )}
            >
              <AuthProvider {...oidcConfig}>
                <BrowserRouter>
                  <Suspense
                    fallback={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "100vh",
                          width: "100vw",
                        }}
                      >
                        <CircularProgress sx={{ color: "#b3b3b3" }} />
                      </div>
                    }
                  >
                    <Routes>
                      <Route path="/callback" element={<Callback />}></Route>
                      <Route path="/*" element={<ProtectedInner />}></Route>
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </AuthProvider>
            </SnackbarProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}

const ProtectedInner = withAuthenticationRequired(Inner, {
  OnRedirecting: () => <Callback />,
});

function Inner() {
  // const isOnline = useOnlineStatus();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = event.data as any;
      console.log("Message received from SW", data);
      if (data?.eventType === "notificationRecieved") {
        enqueueSnackbar(`${data.data.title}`, {
          variant: "info",
        });
      }
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", handler);
      return () =>
        navigator.serviceWorker.removeEventListener("message", handler);
    }
  }, [enqueueSnackbar]);

  return (
    <UserProvider>
      <RouterProvider>
        <Layout>
          {/* {!isOnline && (
        <Alert severity="warning" className="mb-4">
          Sei offline. Alcune funzionalità potrebbero non essere disponibili.
        </Alert>
      )} */}
          <Routes>
            <Route path="/" element={<Navigate to="/vendite" replace />} />
            <Route path="/vendite" element={<Vendite />} />
            <Route path="/spedizioni" element={<Spedizioni />} />
            <Route
              path="/scheda-punto-vendita"
              element={<SchedaPuntoVendita />}
            />
            <Route
              path="/immagini-punto-vendita"
              element={<ImmaginiPuntoVendita />}
            />
            <Route
              path="/repository-immagini"
              element={<RepositoryImmagini />}
            />
            <Route
              path="/repository-immagini/download"
              element={<DownloadImages />}
            />
            <Route path="/statistiche" element={<Statistiche />}>
              <Route index element={<Navigate to="cliente/ordinato" replace />} />
              <Route path="agente">
                 <Route path="visitatori" element={<Visitatori />} />
                 <Route path="acquisti" element={<Acquisti />} />
                 <Route path="carrelli-abbandonati" element={<CarrelliAbbandonati />} />
              </Route>
              <Route path="cliente">
                 <Route path="ordinato" element={<Ordinato />} />
                 <Route path="bestseller" element={<BestSeller />} />
              </Route>
            </Route>

            <Route path="/logout" element={<Logout />} />
            {/* Fallback per rotte non trovate */}
            <Route path="*" element={<Navigate to="/vendite" replace />} />
          </Routes>
        </Layout>
      </RouterProvider>
    </UserProvider>
  );
}

function Callback() {
  const { isLoading, isAuthenticated, error } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (isLoading || error) {
      return;
    }
    if (isAuthenticated) {
      navigate("/");
      return;
    }
    // navigate("/logged-out");
  }, [isLoading, navigate, isAuthenticated, error]);
  if (error) {
    return <div>Errore durante il login: {error.message}</div>;
  }
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        width: "100vw",
      }}
    >
      <CircularProgress sx={{ color: "#b3b3b3" }} />
    </div>
  );
}

export default App;
